import { translateBackend } from '@assets/i18n/config';
import type { TranslatedString } from '@interfaces/shared';
import { TRADING_PROCESS_LIST } from '@interfaces/tradingProcessList';
import type {
  HouseholdModelPortfolioModel,
  HouseholdModelPortfolioWidgetOptions,
} from '@ovComponents/4-module/widgets/householdModelPortfolio/householdModelPortfolio.types';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { Alert, Box, Typography, useTheme } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { HouseholdModelPortfolioDraftPreview } from './householdModelPortfolioDraftPreview';

/**
 * Preview field visibility toggles and label overrides for the Initialize step.
 * Keep in sync with workflow-service's initializeHouseholdModelPortfolioOptions
 * (src/useCases/subStepOptions/initializeHouseholdModelPortfolio.ts) under the
 * same key names.
 */
export type InitializeHouseholdModelPortfolioOptions = Pick<
  HouseholdModelPortfolioWidgetOptions,
  | 'showModelStrategy'
  | 'modelStrategyLabel'
  | 'showModelName'
  | 'modelNameLabel'
  | 'showModelFamily'
  | 'modelFamilyLabel'
  | 'showRiskProfile'
  | 'showRiskReturnLevel'
  | 'showSuggestible'
  | 'showZeroPercentCash'
  | 'showCashEquivalentSecurity'
  | 'showLowThresholdScore'
  | 'showMinimumScore'
  | 'showMaximumScore'
  | 'showForecastedRateOfReturn'
  | 'showForecastedStandardDeviation'
  | 'showFundamentalDetailsLink'
  | 'showDescription'
  | 'descriptionLabel'
  | 'showInvestmentObjective'
  | 'investmentObjectiveLabel'
  | 'showLiquidity'
  | 'liquidityLabel'
  | 'showDisclaimer'
  | 'disclaimerLabel'
  | 'showTargetAllocations'
  | 'targetAllocationsLabel'
> & {
  title?: TranslatedString;
  subtitle?: TranslatedString;
};

export type InitializeHouseholdModelPortfolioSummary = {
  parentModelName?: TranslatedString;
  tradingProcess?: string;
};

export type InitializeHouseholdModelPortfolioVisualProps = {
  options: InitializeHouseholdModelPortfolioOptions;
  loading?: boolean;
  onNext: () => void | Promise<void>;
  draftModel?: HouseholdModelPortfolioModel;
  summary?: InitializeHouseholdModelPortfolioSummary;
};

const SummaryRow = ({
  label,
  value,
  dataTestId,
}: {
  label: string;
  value: string;
  dataTestId?: string;
}) => (
  <Box
    data-testid={dataTestId}
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: 2,
      py: 0.5,
    }}
  >
    <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
      {label}
    </Typography>
    <Typography variant='bodyMedium' sx={{ fontWeight: 600 }}>
      {value}
    </Typography>
  </Box>
);

export const InitializeHouseholdModelPortfolioVisual = ({
  options,
  loading = false,
  onNext,
  draftModel,
  summary,
}: InitializeHouseholdModelPortfolioVisualProps) => {
  const { t } = useTranslation('workflowCompletions');
  const theme = useTheme();

  // Only known once select-parent has written real selections into context
  const hasSelection = Boolean(
    summary?.parentModelName || summary?.tradingProcess,
  );

  return (
    <WorkflowCompletionForm
      onSubmit={onNext}
      submitButtonProps={{
        label: t('householdInitializeShells.initializeButton'),
        // No draft means select-parent hasn't seeded a model yet — there's
        // nothing to review or commit, so block advancing (the info alert below
        // explains why) rather than letting the user skip the review.
        disabled: loading || !draftModel,
        dataTestId: 'initialize-household-model-portfolio-continue-button',
      }}
    >
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {translateBackend(options?.title)}
      </Typography>
      <Typography variant='bodyLarge' sx={{ mb: 3 }}>
        <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
          {translateBackend(options?.subtitle) || ''}
        </ReactMarkdown>
      </Typography>
      {hasSelection && (
        <Box
          data-testid='household-initialize-summary'
          sx={{
            p: 2,
            mb: 3,
            background: theme.color.surfaceContainerVariant,
            borderRadius: theme.borderRadius.md,
          }}
        >
          <Typography variant='headingSmall' sx={{ mb: 1 }}>
            {t('householdInitializeShells.summary.title')}
          </Typography>
          {summary?.parentModelName && (
            <SummaryRow
              dataTestId='household-initialize-summary-parent-model'
              label={t('householdInitializeShells.summary.parentModel')}
              value={translateBackend(summary.parentModelName)}
            />
          )}
          {summary?.tradingProcess && (
            <SummaryRow
              dataTestId='household-initialize-summary-trading-process'
              label={t('householdInitializeShells.summary.tradingProcess')}
              value={
                TRADING_PROCESS_LIST.find(
                  (option) => option.value === summary.tradingProcess,
                )?.name ?? summary.tradingProcess
              }
            />
          )}
        </Box>
      )}
      {draftModel ? (
        <HouseholdModelPortfolioDraftPreview
          model={draftModel}
          options={options}
        />
      ) : (
        <Alert severity='info'>
          {t('householdInitializeShells.noDraftSelected')}
        </Alert>
      )}
    </WorkflowCompletionForm>
  );
};
